import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui';
import { ROLE_MAP_COLUMNS, type RoleMapStatusFilter } from './types';
import { RoleMapFilterButton, RoleMapMenuOption } from './RoleMapFilterButton';
import type { RoleMapTableVM } from './useRoleMapTable';

/**
 * EAA-1837: the toolbar row of `role-map-table.html` — search, "Tình trạng",
 * "Ứng dụng", "Phạm vi", "Xoá bộ lọc" and the right-aligned "Cột" picker. Copy
 * is verbatim from that export.
 *
 * Takes the whole view model (the hook-model prop pattern in the root
 * CLAUDE.md): this one region drives eleven pieces of state, and threading them
 * as individual props would put this component well past the 6–8 prop limit
 * without making anything clearer.
 */
export function RoleMapToolbar({ vm }: { vm: RoleMapTableVM }) {
    const { t } = useTranslation();
    const STATUS_LABELS: Record<RoleMapStatusFilter, string> = {
        normal: t('console.roleMap.status.normal'),
        stale: t('console.roleMap.status.stale'),
    };
    const scopeLabel =
        vm.scope === 'default'
            ? t('console.roleMap.toolbar.scopeDefaultLabel')
            : (vm.actingOrgName ?? t('console.roleMap.toolbar.scopeGenericFallback'));

    return (
        <div className="rm-toolbar">
            {/* EAA-1817: raw <input> with a hand-drawn magnifier → kit Input, icon
                moved into its `prefix` slot. */}
            <Input
                className="rm-toolbar__search"
                ref={vm.searchInputRef}
                value={vm.search}
                onChange={(event) => vm.setSearch(event.target.value)}
                placeholder={t('console.roleMap.toolbar.searchPlaceholder')}
                aria-label={t('console.roleMap.toolbar.searchAriaLabel')}
                prefix={
                    <svg
                        viewBox="0 0 24 24"
                        width="15"
                        height="15"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.8}
                        aria-hidden="true"
                    >
                        <circle cx="11" cy="11" r="7" />
                        <path d="m20 20-3.5-3.5" strokeLinecap="round" />
                    </svg>
                }
            />

            <RoleMapFilterButton
                label={t('console.roleMap.toolbar.statusFilterLabel')}
                icon="add-leading"
                monoChips={false}
                active={vm.statusFilter.length > 0}
                chips={vm.statusFilter.map((value) => STATUS_LABELS[value])}
                open={vm.openFilter === 'status'}
                onOpenChange={(open) => vm.setOpenFilter(open ? 'status' : null)}
            >
                <div className="rm-menu">
                    <span className="rm-menu__label">
                        {t('console.roleMap.toolbar.statusFilterLabel')}
                    </span>
                    {(['normal', 'stale'] as RoleMapStatusFilter[]).map((value) => (
                        <RoleMapMenuOption
                            key={value}
                            checked={vm.statusFilter.includes(value)}
                            label={STATUS_LABELS[value]}
                            onToggle={() => vm.toggleStatus(value)}
                        />
                    ))}
                </div>
            </RoleMapFilterButton>

            <RoleMapFilterButton
                label={t('console.roleMap.toolbar.appFilterLabel')}
                icon="add-leading"
                active={vm.appFilter.length > 0}
                chips={vm.appFilter}
                open={vm.openFilter === 'apps'}
                onOpenChange={(open) => vm.setOpenFilter(open ? 'apps' : null)}
            >
                <div className="rm-menu rm-menu--scroll">
                    <span className="rm-menu__label">
                        {t('console.roleMap.toolbar.appFilterLabel')}
                    </span>
                    {vm.apps.length === 0 && (
                        <span className="rm-menu__empty">
                            {t('console.roleMap.toolbar.appsEmptyHint')}
                        </span>
                    )}
                    {vm.apps.map((app) => (
                        <RoleMapMenuOption
                            key={app.code}
                            checked={vm.appFilter.includes(app.code)}
                            label={app.code}
                            hint={app.name}
                            onToggle={() => vm.toggleApp(app.code)}
                        />
                    ))}
                </div>
            </RoleMapFilterButton>

            <RoleMapFilterButton
                label={t('console.roleMap.toolbar.scopeFilterLabel')}
                icon="chevron-trailing"
                active
                value={scopeLabel}
                open={vm.openFilter === 'scope'}
                onOpenChange={(open) => vm.setOpenFilter(open ? 'scope' : null)}
            >
                <div className="rm-menu">
                    <span className="rm-menu__label">
                        {t('console.roleMap.toolbar.scopeMenuLabel')}
                    </span>
                    {/* EAA-1837: only two slices exist, and they are NOT symmetric —
                        the org slice is the only one the bulk endpoints can touch
                        (see useRoleMapTable's docstring), so each option says what
                        it means rather than just naming itself. An operator without
                        `console.rolemap.manage` never sees the Mặc định option at
                        all (EAA-1817's rule, carried over). */}
                    {vm.canManageDefault && (
                        <RoleMapMenuOption
                            checked={vm.scope === 'default'}
                            label={t('console.roleMap.toolbar.scopeDefaultLabel')}
                            hint={t('console.roleMap.toolbar.scopeDefaultHint')}
                            onToggle={() => {
                                vm.setScope('default');
                                vm.setOpenFilter(null);
                            }}
                        />
                    )}
                    <RoleMapMenuOption
                        checked={vm.scope === 'org'}
                        label={vm.actingOrgName ?? t('console.roleMap.toolbar.scopeOrgFallback')}
                        hint={t('console.roleMap.toolbar.scopeOrgHint')}
                        onToggle={() => {
                            vm.setScope('org');
                            vm.setOpenFilter(null);
                        }}
                    />
                </div>
            </RoleMapFilterButton>

            {vm.filtersActive && (
                <button type="button" className="rm-toolbar__clear" onClick={vm.clearFilters}>
                    <svg
                        viewBox="0 0 24 24"
                        width="14"
                        height="14"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        aria-hidden="true"
                    >
                        <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" />
                    </svg>
                    {t('console.roleMap.toolbar.clearFilters')}
                </button>
            )}

            <span className="rm-toolbar__spacer" />

            <RoleMapFilterButton
                label={t('console.roleMap.toolbar.columnsLabel')}
                icon="columns-trailing"
                active
                open={vm.openFilter === 'columns'}
                onOpenChange={(open) => vm.setOpenFilter(open ? 'columns' : null)}
            >
                <div className="rm-menu">
                    <span className="rm-menu__label">
                        {t('console.roleMap.toolbar.showColumnsMenuLabel')}
                    </span>
                    {ROLE_MAP_COLUMNS.map((column) => (
                        <RoleMapMenuOption
                            key={column.key}
                            checked={column.pinned || vm.isColumnVisible(column.key)}
                            label={column.label}
                            pinned={column.pinned}
                            title={
                                column.pinned
                                    ? t('console.roleMap.toolbar.pinnedColumnTitle')
                                    : undefined
                            }
                            onToggle={() => vm.toggleColumn(column.key)}
                        />
                    ))}
                    <span className="rm-menu__divider" />
                    <button type="button" className="rm-menu__reset" onClick={vm.resetColumns}>
                        {t('console.roleMap.toolbar.resetColumns')}
                    </button>
                </div>
            </RoleMapFilterButton>
        </div>
    );
}
